import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import {
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';

import { ErrorState, PageHeader, SectionCard, StateDisplay } from '@/components/common';
import { DetailPageSkeleton } from '@/components/common/Skeletons';
import { useToast } from '@/hooks/useToast';

import { useNotificationPreferences } from '../hooks/useNotificationPreferences';
import { metaFor } from '../notificationTypeLabels';
import type { NotificationPreference } from '../types/notifications.types';

/**
 * Notification Settings — the signed-in user controls whether WillnQ EMAILS them
 * about each notification type. Reachable from the account menu by every persona.
 *
 * Mandatory types render locked-on with a "Required" chip; the backend rejects any attempt to disable
 * them, so the UI mirrors that rather than pretending they're editable. Two types are mandatory, both
 * reporting a change to the user's own ACCESS: being invited to a company, and their company access
 * changing. Security emails (password reset, verification) are a separate always-on path and never
 * appear here at all.
 *
 * Every row on this page corresponds to a notification the platform actually raises AND can address to
 * this account. Neither half is incidental. The list is built by enumerating the backend enum, so a type
 * with no producer would render as a toggle that silently does nothing — eight such rows were removed,
 * six for unbuilt modules plus a "Platform announcements" row with no authoring surface and an "Offers"
 * row with no producer. The server then filters that enumeration by AUDIENCE
 * (`NotificationTypePolicy.AudienceFor`), because completeness alone offered a candidate the recruiter's
 * controls: "Stalled applications — when an application assigned to you has had no activity" is
 * addressed to the assigned recruiter and can never reach a candidate.
 *
 * So the row set is entirely the server's decision and this page has no persona logic of its own — the
 * same mutation that backs each toggle refuses a type outside the caller's audience, so there is no
 * client-side rule that could drift from the API's answer.
 *
 * Only the EMAIL channel is exposed (H-2): in-app notifications are always stored and shown (the delivery
 * guarantee), so a per-type in-app toggle would have no observable effect — a control that does nothing is
 * worse than none. The backend still stores an in-app flag for forward compatibility; we simply pass its
 * current value through untouched. Each toggle saves immediately; on failure the list refetches, so the
 * switch always reflects the true server state.
 */
export function NotificationSettingsPage() {
  const { preferences, loading, error, refetch, update, saving } = useNotificationPreferences();
  const toast = useToast();

  // A persona can legitimately have nothing to configure: a platform admin receives only the two
  // mandatory access-fact types, which render locked. Promising them a choice they do not have —
  // "turn email on or off for each type" above two disabled switches — reads as a broken page, so the
  // subtitle says what is actually true. Derived, not state: it follows from the rows themselves.
  const hasConfigurableRow = preferences.some((pref) => !pref.isMandatory);

  const onToggleEmail = async (pref: NotificationPreference, next: boolean) => {
    const errors = await update({
      type: pref.type,
      inAppEnabled: pref.inAppEnabled, // preserved as-is; not user-editable (H-2)
      emailEnabled: next,
    });
    if (errors.length > 0) toast.showError(errors[0].message);
    else toast.showSuccess('Preferences updated.');
  };

  if (loading) return <DetailPageSkeleton />;
  if (error) {
    return <ErrorState title="Could not load your notification settings" onRetry={() => void refetch()} />;
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Notifications"
        description="Choose which emails you'd like to receive from WillnQ. Only the notifications your account can receive are listed. In-app notifications always appear in your bell, and security emails such as password resets are always sent."
      />

      {preferences.length === 0 ? (
        <StateDisplay
          tone="secondary"
          icon={<NotificationsNoneOutlinedIcon />}
          title="No notification settings yet"
          description="There's nothing to configure right now."
        />
      ) : (
        <SectionCard
          title="Email preferences"
          subtitle={
            hasConfigurableRow
              ? 'Turn email on or off for each type of notification.'
              : 'The notifications your account receives are all required, so there’s nothing to turn off here.'
          }
        >
          <Stack divider={<Divider flexItem />} spacing={0}>
            {preferences.map((pref) => {
              const meta = metaFor(pref.type);
              return (
                <Stack
                  key={pref.type}
                  direction={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  spacing={{ xs: 1.5, sm: 3 }}
                  sx={{ py: 2 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography variant="body1" fontWeight={600}>
                        {meta.label}
                      </Typography>
                      {pref.isMandatory ? (
                        <Chip label="Required" size="small" color="default" variant="outlined" />
                      ) : null}
                    </Stack>
                    {meta.description ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {meta.description}
                      </Typography>
                    ) : null}
                  </Box>

                  <Stack
                    direction="row"
                    spacing={{ xs: 2, sm: 3 }}
                    sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'auto' } }}
                  >
                    <FormControlLabel
                      labelPlacement="top"
                      sx={{ m: 0, alignItems: 'center' }}
                      control={
                        <Switch
                          checked={pref.emailEnabled}
                          disabled={pref.isMandatory || saving}
                          onChange={(e) => void onToggleEmail(pref, e.target.checked)}
                          inputProps={{ 'aria-label': `Email notifications for ${meta.label}` }}
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                      }
                    />
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </SectionCard>
      )}
    </Stack>
  );
}
